import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Mobile-optimized setup page for iOS devices
 * This page is opened when scanning QR code or clicking invitation link
 */
const MobileSetup: React.FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>();

  const [computerIP, setComputerIP] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Detect if user is on iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) {
      alert('This setup is designed for iPhone/iPad. Please open this link on your iOS device.');
    }

    // Try to detect computer IP from referrer
    try {
      const url = new URL(document.referrer || window.location.href);
      if (url.hostname !== 'localhost') {
        setComputerIP(url.hostname);
      }
    } catch (e) {
      // Ignore errors
    }
  }, []);


  return (
    <div className="flex-1 bg-gradient-to-b from-blue-500 to-purple-600 px-4 py-8">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center text-white mb-8">
          <div className="text-6xl mb-4">⌚</div>
          <h1 className="text-3xl font-bold mb-2">Apple Watch Setup</h1>
          <p className="text-blue-100">Quick setup for Edge-on-Age</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            📱 One-Tap Setup
          </h2>

          {!computerIP ? (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter your computer's IP address:
              </label>
              <input
                type="text"
                value={computerIP}
                onChange={(e) => setComputerIP(e.target.value)}
                placeholder="192.168.1.100"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-lg"
              />
              <p className="text-xs text-gray-500 mt-2">
                Find on Mac: Terminal → <code className="bg-gray-100 px-1">ipconfig getifaddr en0</code>
              </p>
            </div>
          ) : (
            <div className="mb-4">
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                <div>
                  <div className="text-xs text-green-600 font-medium">Connecting to:</div>
                  <div className="text-sm font-mono text-green-900">{computerIP}:3003</div>
                </div>
                <button
                  onClick={() => setComputerIP('')}
                  className="text-xs text-blue-600"
                >
                  Change
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Step 1: Open Shortcuts App */}
            <div className="bg-blue-50 border-2 border-blue-500 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <div className="text-2xl">1️⃣</div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-2">Open Shortcuts App</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    Open the Shortcuts app on your iPhone and tap the + button to create a new shortcut.
                  </p>
                  <div className="bg-blue-100 rounded-lg p-2 text-xs text-blue-800">
                    💡 The Shortcuts app comes pre-installed on all iPhones
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Add Health Actions */}
            <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <div className="text-2xl">2️⃣</div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-2">Add Health Data Actions</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    Tap "Add Action" and search for each of these:
                  </p>

                  <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside mb-3">
                    <li><strong>Find Health Samples</strong> → Select "Heart Rate" → Last 1 Hour</li>
                    <li><strong>Find Health Samples</strong> → Select "Oxygen Saturation" → Last 1 Hour</li>
                    <li><strong>Find Health Samples</strong> → Select "Respiratory Rate" → Last 1 Hour</li>
                    <li><strong>Find Health Samples</strong> → Select "Body Temperature" → Last 1 Hour</li>
                  </ul>

                  <div className="bg-purple-100 rounded-lg p-2 text-xs text-purple-800">
                    💡 These actions will request Health permissions when first run
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3: Add API Call */}
            <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <div className="text-2xl">3️⃣</div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-2">Add API Call Action</h3>
                  <p className="text-sm text-gray-700 mb-3">
                    Add a "Get Contents of URL" action with these settings:
                  </p>

                  <div className="bg-white rounded-lg p-3 mb-3 text-xs font-mono space-y-2">
                    <div><strong>URL:</strong> http://{computerIP}:3003/api/integrations/apple-health/sync</div>
                    <div><strong>Method:</strong> POST</div>
                    <div><strong>Request Body:</strong> JSON</div>
                    <div className="mt-2 bg-gray-50 p-2 rounded overflow-x-auto">
                      <pre>{`{
  "deviceId": "${deviceId}",
  "vitals": [Health Samples]
}`}</pre>
                    </div>
                  </div>

                  <p className="text-xs text-gray-600 italic">
                    Replace [Health Samples] with the variable from step 2
                  </p>
                </div>
              </div>
            </div>

            {/* Step 4: Test & Automate */}
            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <div className="text-2xl">4️⃣</div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-2">Test & Automate</h3>
                  <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                    <li>Name your shortcut "Sync to Edge-on-Age" and tap Done</li>
                    <li>Run it once manually to grant Health permissions</li>
                    <li>You should see a success response from the server</li>
                    <li>Set up automation: Automation tab → Time of Day → Every Hour</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="w-full mt-4 px-4 py-2 text-blue-600 text-sm font-medium"
          >
            {showInstructions ? '▼ Hide' : '▶'} Detailed Instructions
          </button>

          {showInstructions && (
            <div className="mt-4 bg-gray-50 rounded-lg p-4 text-sm text-gray-700">
              <h4 className="font-semibold mb-3">📖 Complete Step-by-Step Guide:</h4>

              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-blue-900 mb-1">Step 1: Create New Shortcut</p>
                  <p>Open Shortcuts app → Tap + button → Create new shortcut</p>
                </div>

                <div>
                  <p className="font-semibold text-purple-900 mb-1">Step 2: Add Health Data Collection</p>
                  <p className="mb-2">For each health metric, tap "Add Action" and search:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs ml-3">
                    <li>"Find Health Samples" → Heart Rate → Sort by Start Date → Limit to 1</li>
                    <li>"Find Health Samples" → Oxygen Saturation → Sort by Start Date → Limit to 1</li>
                    <li>"Find Health Samples" → Respiratory Rate → Sort by Start Date → Limit to 1</li>
                    <li>"Find Health Samples" → Body Temperature → Sort by Start Date → Limit to 1</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold text-orange-900 mb-1">Step 3: Add API Call</p>
                  <p className="mb-2">Add "Get Contents of URL" action:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs ml-3">
                    <li>URL: http://{computerIP}:3003/api/integrations/apple-health/sync</li>
                    <li>Method: POST</li>
                    <li>Request Body: JSON</li>
                    <li>Add deviceId field with value: {deviceId}</li>
                    <li>Add vitals field with Health Samples variable</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold text-green-900 mb-1">Step 4: Save & Test</p>
                  <ul className="list-disc list-inside space-y-1 text-xs ml-3">
                    <li>Name it "Sync to Edge-on-Age"</li>
                    <li>Tap Done to save</li>
                    <li>Run manually to grant Health permissions</li>
                    <li>Check for success response from server</li>
                  </ul>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-4">
                  <p className="font-semibold text-blue-900 text-xs mb-1">⚙️ Optional Automation:</p>
                  <p className="text-xs text-blue-800">
                    Shortcuts app → Automation tab → + → Time of Day → Every Hour → Run "Sync to Edge-on-Age"
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Help Card */}
        <div className="bg-white bg-opacity-90 rounded-xl p-4 text-sm">
          <h3 className="font-semibold text-gray-900 mb-2">Need Help?</h3>
          <ul className="space-y-1 text-gray-700">
            <li>• Make sure iPhone and computer on same WiFi</li>
            <li>• Check Health permissions in Settings → Privacy</li>
            <li>• Wear your Apple Watch for data collection</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="text-center text-white text-xs mt-6 opacity-75">
          Powered by Edge-on-Age — Health Stream Analytics, LLC
        </div>
      </div>
    </div>
  );
};

export default MobileSetup;
